import { App } from "./App";
import React from "react";
import * as Sentry from "@sentry/react";
import { ApolloProvider } from "@apollo/client/react";
import { ContextProvider } from "@domain/context";
import { ThemeProvider } from "@domain/context/ThemeProvider";
import { ScrollbarDetection } from "@domain/infrastructure/helpers/browser";
import { createRoot } from "react-dom/client";
import { getApolloClient } from "@domain/graphql";
import { getEnvCode } from "@domain/infrastructure/getEnvCode";
import { AllCommunityModule, ModuleRegistry, enableDevValidations } from "ag-grid-community";
import { I18nProvider } from "@react-aria/i18n";

ModuleRegistry.registerModules([AllCommunityModule]);

// Warm the pdf stack in the background once the page has loaded: fetching the
// pdfjs chunks, spinning up the shared worker (its hashed .mjs script would
// otherwise 404 in tabs that stay open across a deploy) and pre-importing the
// ViewerView chunk so the viewer opens with no loading gap.
const prewarmPdfViewer = (): void => {
    void import("@domain/pdf/sharedPdfWorker")
        .then(({ getSharedPdfWorker }) => {
            getSharedPdfWorker();
        })
        .catch(() => {
            // best-effort: a failed prewarm only means the pdf stack loads on first use
        });
    void import("@app/pages/ViewerView").catch(() => {
        // best-effort: a failed prewarm only means the viewer loads on navigation
    });
};
if (document.readyState === "complete") {
    prewarmPdfViewer();
} else {
    window.addEventListener("load", prewarmPdfViewer, { once: true });
}

// Lazily loaded route chunks 404 in already-open tabs after a deploy replaces
// the hashed asset names; reload once to pick up the new index.html. The
// timestamp guard prevents a reload loop when the failure is not deploy-related.
window.addEventListener("vite:preloadError", (event) => {
    const key = "vite:preloadError:reloadedAt";
    const lastReload = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - lastReload < 30_000) {
        return;
    }
    sessionStorage.setItem(key, String(Date.now()));
    event.preventDefault();
    window.location.reload();
});

// v36 dropped ValidationModule from AllCommunityModule to slim production bundles.
// Restore full validation diagnostics in development only (tree-shaken from prod builds).
if (import.meta.env.DEV) {
    enableDevValidations();
}

Sentry.init({
    enabled: document.location.host.indexOf("localhost") === -1,
    dsn: "https://1c5a009031aac24fdc98cf19e42d7109@o4511897115426816.ingest.de.sentry.io/4511902707613776",
    environment: getEnvCode(),
    ignoreErrors: [
        "Non-Error exception captured",
        "ResizeObserver loop",
        "Failed to update a ServiceWorker for scope",
        "AbortError: Transition was skipped",
        "InvalidStateError: Transition was aborted because of invalid state",
        "The operation was aborted.",
        "Skipped ViewTransition due to another transition starting",
        "Skipped ViewTransition due to document being hidden",
    ],
});
Sentry.setTag("appVersion", import.meta.env.VITE_VERSION);

ScrollbarDetection.runDetector();

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
    <ThemeProvider>
        <I18nProvider locale="de-DE">
            <ApolloProvider client={getApolloClient()}>
                <ContextProvider>
                    <App />
                </ContextProvider>
            </ApolloProvider>
        </I18nProvider>
    </ThemeProvider>,
);
